import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'

/**
 * Merge Tailwind class names safely.
 * Combines clsx (conditional classes) + tailwind-merge (deduplicate conflicting utilities).
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Format a date string or Date object for display.
 */
export function formatDate(
  date: string | Date | null | undefined,
  formatStr = 'dd MMM yyyy',
): string {
  if (!date) return '—'
  try {
    const d = typeof date === 'string' ? parseISO(date) : date
    return format(d, formatStr, { locale: tr })
  } catch {
    return '—'
  }
}

/**
 * Format as Turkish long date with time.
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  return formatDate(date, 'dd MMM yyyy, HH:mm')
}

/**
 * Human-readable relative time in Turkish.
 * e.g. "3 gün önce"
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '—'
  try {
    const d = typeof date === 'string' ? parseISO(date) : date
    return formatDistanceToNow(d, { addSuffix: true, locale: tr })
  } catch {
    return '—'
  }
}

/**
 * Format a number as Turkish Lira currency.
 * e.g. 1500 → "₺1.500,00"
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency = 'TRY',
): string {
  if (amount === null || amount === undefined) return '—'
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format a Turkish phone number.
 * Input: "05321234567" or "+905321234567"
 * Output: "0532 123 45 67"
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—'
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('0')) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`
  }
  if (digits.length === 12 && digits.startsWith('90')) {
    const local = '0' + digits.slice(2)
    return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7, 9)} ${local.slice(9)}`
  }
  return phone
}

/**
 * Format large numbers with Turkish locale separators.
 * e.g. 1500 → "1.500"
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('tr-TR').format(value)
}

/**
 * Truncate a string to maxLength, appending "…" if needed.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 1) + '…'
}

/**
 * Generate initials from a full name.
 * e.g. "Ahmet Yılmaz" → "AY"
 */
export function getInitials(name: string | null | undefined, max = 2): string {
  if (!name) return '?'
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, max)
    .map((w) => w[0].toUpperCase())
    .join('')
}

/**
 * Clamp a value between min and max.
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Sleep utility for async flows.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Download a blob as a file in the browser.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Convert object to query string, skipping null/undefined values.
 */
export function toQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, String(value))
    }
  }
  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}
